import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import SmartRecordsLandingSection from "../components/SmartRecordsLandingSection";
import {
  ArrowRight, Sparkles, Play, Check, X, ChevronDown, Phone, Mic, Calendar,
  RefreshCw, TrendingUp, Users, Clock, MessageSquare, Bell, Award, Euro,
  ShieldCheck, Bot, BarChart3, CheckCircle2, Activity, PhoneCall, Zap, Star,
  Crown, Headphones, Building2, Database, Layers, FileText,
  ScanLine, FileSignature, Wallet,
} from "lucide-react";

// ---------- Static config ----------
const TOTAL_FAQ = 5;

const ROI_BOXES = [
  { label: "3 igieni recuperate / mese", value: "€240", sub: "80€ cad." },
  { label: "2 no-show evitati", value: "€300", sub: "150€ ogni mancata visita" },
  { label: "10 recall convertiti", value: "Agenda piena", sub: "+22% occupazione" },
];

const PAIN_POINTS = [
  { icon: Phone, text: "Telefonate continue durante il lavoro" },
  { icon: Calendar, text: "Pazienti che spostano appuntamenti all'ultimo" },
  { icon: Bell, text: "No-show che lasciano buchi agenda" },
  { icon: RefreshCw, text: "Richiami igiene dimenticati" },
  { icon: Euro, text: "Preventivi mai ricontattati" },
  { icon: MessageSquare, text: "Staff sommerso dai messaggi WhatsApp" },
];

const PILLARS = [
  { icon: Mic, emoji: "🎙️", title: "AI che comprende i vocali WhatsApp",
    text: "I pazienti inviano vocali? SmartBooker li ascolta, li capisce e risponde automaticamente.",
    color: "from-violet-500 to-fuchsia-600" },
  { icon: Calendar, emoji: "📅", title: "Agenda intelligente poltrone e operatori",
    text: "Gestisci sale, poltrone, dentisti, igienisti e disponibilità in un unico calendario.",
    color: "from-blue-500 to-cyan-600" },
  { icon: RefreshCw, emoji: "🔁", title: "Recall automatici pazienti",
    text: "Igiene, controlli periodici, follow-up e richiami inviati automaticamente al momento giusto.",
    color: "from-emerald-500 to-teal-600" },
  { icon: TrendingUp, emoji: "📈", title: "Recupero pazienti inattivi",
    text: "Riattiva pazienti che non tornano da mesi con messaggi personalizzati e orari intelligenti.",
    color: "from-amber-500 to-orange-600" },
];

const FEATURES_PREMIUM = [
  { icon: Clock, text: "Prenotazioni online H24" },
  { icon: Bell, text: "Reminder automatici visita" },
  { icon: MessageSquare, text: "Conferma appuntamenti WhatsApp" },
  { icon: Calendar, text: "Gestione poltrone multiple" },
  { icon: Users, text: "Multi-operatore" },
  { icon: RefreshCw, text: "Recall igiene semestrale" },
  { icon: TrendingUp, text: "Riattivazione pazienti inattivi" },
  { icon: Euro, text: "Follow-up preventivi non chiusi" },
  { icon: BarChart3, text: "Dashboard KPI studio" },
  { icon: Award, text: "White label con brand studio" },
];

const COMPARISON = [
  ["Rispondi quando puoi", "Risponde 24/7"],
  ["Richiami manuali", "Recall automatici"],
  ["Agenda dispersiva", "Agenda centralizzata"],
  ["No-show frequenti", "Reminder intelligenti"],
  ["Tempo staff perso", "Staff libero e produttivo"],
];

const FAQS = [
  { q: "È difficile da usare?", a: "No, è pensato per reception e staff non tecnico. Bastano pochi minuti per imparare. Abbiamo un onboarding guidato dedicato per studi dentistici." },
  { q: "Posso usarlo con più poltrone?", a: "Sì, gestione multi-posto completa: ogni poltrona, sala igiene o studio ha la sua agenda, incrociata con operatori e servizi." },
  { q: "Funziona con WhatsApp?", a: "Sì, integrazione nativa con WhatsApp Business. Il bot risponde, prenota, sposta appuntamenti e capisce anche i messaggi vocali grazie all'AI." },
  { q: "Quanto tempo ci vuole per attivarlo?", a: "Pochi minuti. Configurazione studio, servizi, operatori e poltrone sono pre-impostate se vieni dalla demo odontoiatrica." },
  { q: "Serve carta di credito?", a: "No. Prova gratuita 14 giorni senza carta, senza vincoli, senza rinnovo automatico." },
];

// ============================================================
// Main component
// ============================================================
const DentistaLanding = () => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => `dlp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [scrollDepth, setScrollDepth] = useState(0);
  const [exitShown, setExitShown] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const exitTrackedRef = useRef(false);
  const pageLoadTsRef = useRef(Date.now());
  const maxScrollRef = useRef(0);

  // ---------- Exit popup cooldown (7 days) ----------
  const EXIT_POPUP_STORAGE_KEY = "sb_exit_popup_dentista_dismissed_at";
  const EXIT_POPUP_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  const wasRecentlyDismissed = () => {
    try {
      const ts = localStorage.getItem(EXIT_POPUP_STORAGE_KEY);
      if (!ts) return false;
      return (Date.now() - parseInt(ts, 10)) < EXIT_POPUP_COOLDOWN_MS;
    } catch (e) { return false; }
  };

  const markDismissed = () => {
    try { localStorage.setItem(EXIT_POPUP_STORAGE_KEY, String(Date.now())); } catch (e) {}
  };

  // ---------- Tracking helper ----------
  const track = (event, data = {}) => {
    axios.post(`${API}/demo/events/track`, {
      session_id: sessionId,
      variant: "dentista-landing",
      event,
      data,
    }).catch(() => {});
    // Mirror to unified landing analytics (Super Admin dashboard)
    axios.post(`${API}/public/landing/track`, {
      event_type: event, landing: "dentista", session_id: sessionId, meta: data,
    }).catch(() => {});
  };

  useEffect(() => {
    // SEO meta for sharing
    document.title = "SmartBooker per Studi Dentistici | Più pazienti, meno stress";
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute(attr, name); document.head.appendChild(tag); }
      tag.setAttribute("content", content);
    };
    setMeta("description", "La piattaforma che automatizza il tuo studio dentistico: prenotazioni online, reminder WhatsApp, recall pazienti. Demo gratis.");
    setMeta("og:title", "SmartBooker per Studi Dentistici", true);
    setMeta("og:description", "Più pazienti. Più entrate. Meno stress. Demo gratuita.", true);
    setMeta("og:image", "https://smartbooker.it/og-cover-dentista.png", true);
    setMeta("og:image:width", "1200", true);
    setMeta("og:image:height", "630", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", "https://smartbooker.it/og-cover-dentista.png");

    track("page_view", { ref: document.referrer || "" });
    // scroll depth tracking
    const onScroll = () => {
      const pct = (window.scrollY + window.innerHeight) / document.body.scrollHeight * 100;
      if (pct > maxScrollRef.current) maxScrollRef.current = pct;
      if (pct >= 50 && scrollDepth < 50) { track("scroll_50"); setScrollDepth(50); }
      if (pct >= 90 && scrollDepth < 90) { track("scroll_90"); setScrollDepth(90); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Exit intent popup — desktop only, with strict guards to avoid spam:
    // 1. User must have been on the page ≥ 20s
    // 2. User must have scrolled ≥ 40% of content
    // 3. Mouse must actually leave the window (relatedTarget === null)
    // 4. Not shown in last 7 days (localStorage)
    // 5. Only once per page load (exitShown state)
    const onMouseOut = (e) => {
      if (exitShown || showExitPopup) return;
      if (window.innerWidth < 768) return;
      if (wasRecentlyDismissed()) return;
      // dwell time gate
      if (Date.now() - pageLoadTsRef.current < 20000) return;
      // scroll depth gate
      if (maxScrollRef.current < 40) return;
      // real viewport exit only (cursor left the window, not just moved over an iframe/element)
      if (e.relatedTarget !== null || e.toElement) return;
      if (e.clientY <= 0) {
        setShowExitPopup(true);
        setExitShown(true);
        track("exit_popup_seen");
      }
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onMouseOut);
    };
    // eslint-disable-next-line
  }, []);

  const goTrial = (source) => { track("trial_click", { source }); navigate("/onboarding?sector=dentista&prefill=demo-odonto"); };
  const goTrialPro = (source) => { track("clicked_trial_pricing", { source }); navigate("/onboarding?sector=dentista&plan=pro&prefill=demo-odonto"); };
  const goDemo = (source) => { track("demo_click", { source }); navigate("/demo-odonto"); };
  const goDemoFromPricing = () => { track("clicked_demo_pricing"); navigate("/demo-odonto"); };
  const goEnterprise = () => {
    track("clicked_enterprise");
    window.open("https://wa.me/393770873260?text=Buongiorno%2C%20vorrei%20informazioni%20sulla%20soluzione%20enterprise%20SmartBooker%20per%20il%20mio%20gruppo%20odontoiatrico", "_blank", "noopener");
  };
  const heroClick = (dest) => { track("hero_cta_click", { dest }); dest === "trial" ? goTrial("hero") : goDemo("hero"); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/40 to-slate-950 text-slate-100" data-testid="dentista-landing">

      {/* ============================================================ */}
      {/* Dynamic promo bar (only for ads traffic) */}
      {/* ============================================================ */}
      <PromoBar />

      {/* ============================================================ */}
      {/* Top nav minimale */}
      {/* ============================================================ */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/70 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg">
            <img src="/brand/sb-icon.png" alt="SmartBooker" className="h-9 w-9 drop-shadow-[0_0_12px_rgba(34,211,238,0.35)]" />
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">SmartBooker</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 ml-1">Studi dentistici</span>
          </Link>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => goDemo("nav")} className="text-sm text-slate-300 hover:text-white transition-colors">Demo</button>
            <button onClick={() => goTrial("nav")} className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-sm font-semibold shadow-lg shadow-cyan-500/30" data-testid="nav-cta-trial">
              Prova gratis
            </button>
          </div>
        </div>
      </nav>

      {/* ============================================================ */}
      {/* 1. HERO */}
      {/* ============================================================ */}
      <HeroSection heroClick={heroClick} goDemo={goDemo} />

      {/* ============================================================ */}
      {/* 2. PAIN POINTS */}
      {/* ============================================================ */}
      <PainPointsSection />

      {/* ============================================================ */}
      {/* 3. PILLARS */}
      {/* ============================================================ */}
      <PillarsSection />

      {/* ============================================================ */}
      {/* 3b. DENTAL SUITE (Migration · Odontogram · Smart Records) */}
      {/* ============================================================ */}
      <DentalSuiteSection goDemo={goDemo} />

      {/* ============================================================ */}
      {/* 4. DEMO LIVE */}
      {/* ============================================================ */}
      <DemoLiveSection goDemo={goDemo} />

      {/* ============================================================ */}
      {/* 5. ROI */}
      {/* ============================================================ */}
      <RoiSection />

      {/* ============================================================ */}
      {/* 6. COME FUNZIONA */}
      {/* ============================================================ */}
      <HowItWorksSection />

      {/* ============================================================ */}
      {/* 7. FEATURES */}
      {/* ============================================================ */}
      <FeaturesSection />

      {/* ============================================================ */}
      {/* 8. SOCIAL PROOF */}
      {/* ============================================================ */}
      <SocialProofSection />

      {/* ============================================================ */}
      {/* 9. COMPARISON */}
      {/* ============================================================ */}
      <ComparisonSection />

      {/* ============================================================ */}
      {/* SMART RECORDS — Premium feature highlight */}
      {/* ============================================================ */}
      <SmartRecordsLandingSection
        accent="#06B6D4"
        demoPath="/demo-odonto"
        badge="Studio Dentistico · Paziente da 3 anni"
        clientName="Alessandra Bruno"
        sectorTagline="SmartBooker non è solo agenda. È la cartella clinica intelligente del tuo studio dentistico:"
        highlights={["Anamnesi clinica completa", "Cure effettuate · denti trattati", "Preventivi e piani trattamento", "Documenti clinici · radiografie", "Timeline appuntamenti", "AI summary clinico"]}
        stats={[{ label: "Speso", value: "€570" }, { label: "Cure", value: "6" }, { label: "Ultima", value: "17 apr" }]}
        timeline={["Otturazione 4.6 · completata", "Igiene professionale · completata", "Preventivo €1.420 · in attesa"]}
        aiHint="Allergia penicillina · Igiene da rinnovare. Recall WhatsApp consigliato."
      />

      {/* ============================================================ */}
      {/* 9.5 PRICING */}
      {/* ============================================================ */}
      <PricingSection track={track} goTrialPro={goTrialPro} goDemoFromPricing={goDemoFromPricing} goEnterprise={goEnterprise} />

      {/* ============================================================ */}
      {/* 10. FINAL CTA */}
      {/* ============================================================ */}
      <FinalCtaSection goTrial={goTrial} goDemo={goDemo} />

      {/* ============================================================ */}
      {/* 11. FAQ */}
      {/* ============================================================ */}
      <FaqSection track={track} />

      {/* Footer minimale */}
      <footer className="border-t border-slate-800/60 py-8 px-5 text-center text-xs text-slate-500">
        <p>© 2026 SmartBooker — La piattaforma per studi dentistici moderni</p>
        <p className="mt-1">
          <Link to="/privacy-policy" className="hover:text-slate-300">Privacy</Link> · {" "}
          <Link to="/terms-and-conditions" className="hover:text-slate-300">Termini</Link> · {" "}
          <Link to="/" className="hover:text-slate-300">Tutti i settori</Link>
        </p>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 p-3 bg-gradient-to-t from-slate-950/95 to-slate-950/70 backdrop-blur-md border-t border-slate-800/60" data-testid="mobile-sticky-cta">
        <Button
          onClick={() => goTrial("sticky_mobile")}
          className="w-full h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold text-base shadow-xl shadow-cyan-500/40"
        >
          <Zap className="w-4 h-4 mr-2" /> Prova gratis 14 giorni
        </Button>
      </div>

      {/* Exit intent popup */}
      {showExitPopup && (
        <ExitPopup
          onClose={() => { markDismissed(); setShowExitPopup(false); }}
          onDemo={() => { markDismissed(); track("exit_popup_click", { dest: "demo" }); setShowExitPopup(false); navigate("/demo-odonto"); }}
          onTrial={() => { markDismissed(); track("exit_popup_click", { dest: "trial" }); setShowExitPopup(false); navigate("/onboarding?sector=dentista&prefill=demo-odonto"); }}
        />
      )}
    </div>
  );
};

// ============================================================
// Promo bar
// ============================================================
const PromoBar = () => {
  const isAd = useMemo(() => {
    if (typeof window === "undefined") return false;
    return /utm_|gclid|fbclid/i.test(window.location.search);
  }, []);
  if (!isAd) return null;
  return (
    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-slate-900 text-center text-xs sm:text-sm font-bold py-2 px-4" data-testid="promo-bar">
      <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
      Offerta attiva · onboarding dedicato per studi dentistici <span className="underline">gratuito</span>
    </div>
  );
};

// ============================================================
// Hero
// ============================================================
const HeroSection = ({ heroClick, goDemo }) => (
  <section className="relative overflow-hidden pt-12 pb-20 md:pt-20 md:pb-28 px-5" data-testid="hero-section">
    {/* Glow */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[500px] bg-cyan-500/10 blur-[120px] rounded-full" />
    </div>
    <div className="relative max-w-5xl mx-auto text-center">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-[11px] font-semibold text-cyan-300 mb-6">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
        STUDI DENTISTICI · CENTRI ODONTOIATRICI
      </div>
      <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5">
        La piattaforma che <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">automatizza e fa crescere</span> il tuo studio dentistico
      </h1>
      <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed">
        Prenotazioni online, WhatsApp AI, richiami pazienti, agenda poltrone e riduzione no-show in un solo sistema.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
        <Button
          onClick={() => heroClick("demo")}
          className="h-12 sm:h-14 px-7 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold text-base shadow-xl shadow-cyan-500/30"
          data-testid="hero-cta-demo"
        >
          <Play className="w-4 h-4 mr-2" /> Guarda demo per dentisti
        </Button>
        <Button
          onClick={() => heroClick("trial")}
          variant="outline"
          className="h-12 sm:h-14 px-7 rounded-full border-slate-600 bg-slate-800/40 hover:bg-slate-800 text-slate-100 text-base"
          data-testid="hero-cta-trial"
        >
          Prova gratis 14 giorni <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
      <a href="https://wa.me/393770873260?text=Vorrei%20una%20consulenza%20privata%20per%20il%20mio%20studio%20dentistico"
         target="_blank" rel="noopener noreferrer"
         onClick={() => goDemo("hero_call")}
         className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300 transition-colors"
         data-testid="hero-cta-consult">
        <PhoneCall className="w-3.5 h-3.5" /> o prenota una consulenza privata
      </a>
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-8 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Nessuna carta richiesta</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Attivazione immediata</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Assistenza italiana</span>
      </div>
      <HeroMockup />
    </div>
  </section>
);

const HeroMockup = () => (
  <div className="mt-14 relative rounded-3xl border border-slate-700/50 bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-sm p-4 sm:p-6 shadow-2xl">
    {/* Top KPI strip */}
    <div className="grid grid-cols-4 gap-2 mb-4">
      {[
        { k: "Nuovi pazienti", v: "+18", c: "text-blue-400" },
        { k: "Agenda piena", v: "+22%", c: "text-emerald-400" },
        { k: "No-show", v: "-37%", c: "text-amber-400" },
        { k: "Entrate", v: "€8.460", c: "text-violet-400" },
      ].map((k, i) => (
        <div key={i} className="p-2.5 rounded-xl bg-slate-900/70 border border-slate-700/40 text-left">
          <p className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wider">{k.k}</p>
          <p className={`text-sm sm:text-xl font-bold ${k.c}`}>{k.v}</p>
        </div>
      ))}
    </div>
    {/* Agenda strip */}
    <div className="rounded-2xl bg-slate-950/60 p-3 border border-slate-800/40">
      <div className="grid grid-cols-3 gap-2 text-xs">
        {[
          { chair: "Poltrona 1", t: "10:00", p: "Impianto", name: "G. Neri", color: "from-violet-500/30 to-violet-500/5" },
          { chair: "Poltrona 2", t: "10:00", p: "Prima visita", name: "L. Greco", color: "from-blue-500/30 to-blue-500/5" },
          { chair: "Sala Igiene", t: "10:00", p: "Igiene", name: "A. Russo", color: "from-emerald-500/30 to-emerald-500/5" },
        ].map((a, i) => (
          <div key={i} className={`p-3 rounded-xl bg-gradient-to-br ${a.color} border border-white/5`}>
            <p className="text-[10px] text-slate-400">{a.chair} · {a.t}</p>
            <p className="font-semibold text-slate-100 text-[13px] mt-1">{a.p}</p>
            <p className="text-[11px] text-slate-400">{a.name}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// Pain points
// ============================================================
const PainPointsSection = () => (
  <section className="py-20 px-5 bg-slate-900/30 border-y border-slate-800/40" data-testid="pain-points-section">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12 max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Quanto tempo perde ogni giorno la tua reception?</h2>
        <p className="text-slate-400">Riconosci qualcuno di questi scenari?</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PAIN_POINTS.map((p, i) => (
          <div key={i} className="p-5 rounded-2xl bg-slate-800/40 border border-slate-700/40 hover:border-red-500/40 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-400 flex items-center justify-center flex-shrink-0">
                <p.icon className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-200 font-medium leading-relaxed">{p.text}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-10">
        <p className="text-xl sm:text-2xl font-bold">
          <span className="text-slate-400">SmartBooker</span>{" "}
          <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">automatizza tutto questo.</span>
        </p>
      </div>
    </div>
  </section>
);

// ============================================================
// Pillars
// ============================================================
const PillarsSection = () => (
  <section className="py-20 px-5" data-testid="pillars-section">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14 max-w-3xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Non è un semplice software. <span className="text-cyan-400">È un sistema di crescita</span> per studi dentistici.</h2>
        <p className="text-slate-400">4 pilastri progettati specificamente per il settore dentale.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {PILLARS.map((p, i) => (
          <div key={i} className="relative p-6 rounded-3xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 hover:border-cyan-500/40 transition-all hover:-translate-y-0.5" data-testid={`pillar-${i}`}>
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center text-xl mb-4`}>
              <span>{p.emoji}</span>
            </div>
            <h3 className="text-lg font-bold mb-2 text-white">{p.title}</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// Demo live CTA
// ============================================================
const DemoLiveSection = ({ goDemo }) => (
  <section className="py-20 px-5 bg-gradient-to-br from-slate-900/40 to-slate-950/40 border-y border-slate-800/40" data-testid="demo-live-section">
    <div className="max-w-5xl mx-auto text-center">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-violet-500/40 bg-violet-500/10 text-[11px] font-semibold text-violet-300 mb-5">
        <Sparkles className="w-3 h-3" /> DEMO INTERATTIVA · 2 MINUTI
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Guarda SmartBooker dentro uno studio dentistico reale</h2>
      <p className="text-slate-400 mb-8 max-w-2xl mx-auto">Esplora agenda, WhatsApp AI, richiami e analytics in una demo guidata di 2 minuti.</p>

      <div className="relative max-w-xl mx-auto rounded-3xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-slate-700/50 p-5 sm:p-6 shadow-2xl mb-8">
        <div className="space-y-2 text-left">
          {[
            { c: "Poltrona 1 · 10:00", s: "Impianto", p: "Giuseppe Neri", op: "Dott. Rinaldi", color: "bg-violet-500/15 border-violet-500/40" },
            { c: "Sala Igiene · 10:00", s: "Igiene dentale", p: "Anna Russo", op: "Dr.ssa Greco", color: "bg-emerald-500/15 border-emerald-500/40" },
            { c: "Poltrona 2 · 11:30", s: "Ortodonzia consulto", p: "Sofia Bruno", op: "Dott. Conti", color: "bg-amber-500/15 border-amber-500/40" },
          ].map((a, i) => (
            <div key={i} className={`p-3 rounded-xl border ${a.color} flex items-center justify-between text-sm`}>
              <div>
                <p className="text-slate-300 text-[11px]">{a.c}</p>
                <p className="font-semibold text-white">{a.s}</p>
                <p className="text-slate-400 text-xs">{a.p}</p>
              </div>
              <span className="text-[11px] text-slate-300 bg-slate-900/60 px-2 py-1 rounded-full">{a.op}</span>
            </div>
          ))}
        </div>
      </div>

      <Button
        onClick={() => goDemo("demo_section")}
        className="h-12 px-7 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-600 hover:from-violet-600 hover:to-fuchsia-700 text-white font-semibold text-base shadow-xl shadow-violet-500/30"
        data-testid="demo-cta"
      >
        Entra nella demo odontoiatrica <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  </section>
);

// ============================================================
// ROI
// ============================================================
const RoiSection = () => (
  <section className="py-20 px-5" data-testid="roi-section">
    <div className="max-w-5xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Un solo appuntamento recuperato può ripagarti il software</h2>
      <p className="text-slate-400 mb-10">Numeri reali di studi che già usano SmartBooker.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {ROI_BOXES.map((r, i) => (
          <div key={i} className="p-7 rounded-3xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 text-left" data-testid={`roi-box-${i}`}>
            <div className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full mb-3">
              <TrendingUp className="w-3 h-3" /> VALORE GENERATO
            </div>
            <p className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">{r.value}</p>
            <p className="text-sm font-semibold text-white mt-1">{r.label}</p>
            <p className="text-xs text-slate-400 mt-1">{r.sub}</p>
          </div>
        ))}
      </div>
      <p className="mt-10 text-lg font-semibold text-slate-200">
        SmartBooker non è un costo. <span className="text-cyan-400">È uno strumento che genera valore.</span>
      </p>
    </div>
  </section>
);

// ============================================================
// How it works
// ============================================================
const HowItWorksSection = () => (
  <section className="py-20 px-5 bg-slate-900/30 border-y border-slate-800/40" data-testid="how-section">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Attivi oggi. <span className="text-cyan-400">Lavora da subito.</span></h2>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 relative">
        {[
          { n: 1, t: "Configura studio e servizi", d: "Imposta poltrone, operatori, orari e servizi. Guidato con template odontoiatrico pronto all'uso.", icon: ShieldCheck },
          { n: 2, t: "Collega WhatsApp e agenda", d: "Integrazione WhatsApp Business in 2 click. Il bot AI è già pronto per rispondere.", icon: Bot },
          { n: 3, t: "Ricevi prenotazioni e automatizza richiami", d: "I pazienti prenotano 24/7. Recall, reminder e follow-up partono automaticamente.", icon: Zap },
        ].map((s) => (
          <div key={s.n} className="relative p-6 rounded-3xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-slate-700/50">
            <div className="absolute -top-3 left-6 px-2 py-0.5 rounded-full bg-cyan-500 text-white text-[10px] font-bold tracking-wider">STEP {s.n}</div>
            <s.icon className="w-7 h-7 text-cyan-400 mb-3" />
            <h3 className="font-bold text-white mb-2">{s.t}</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// Dental Suite — Premium pillars (Migration + Odontogram + Smart Records)
// ============================================================
const DentalSuiteSection = ({ goDemo }) => (
  <section className="py-20 sm:py-24 px-5 bg-gradient-to-b from-slate-950 via-violet-950/10 to-slate-950" data-testid="dental-suite-section">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14">
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-cyan-600 text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg">
          <Crown className="w-3 h-3" /> Dental Suite · Enterprise
        </span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mt-5 text-white">
          Una piattaforma <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">odontoiatrica intelligente</span>.<br/>
          Non il solito gestionale.
        </h2>
        <p className="text-slate-400 max-w-2xl mx-auto mt-4 text-base sm:text-lg">
          Sei tecnologie che trasformano lo studio. Dall'acquisizione del paziente alla ricevuta firmata digitalmente.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[
          {
            icon: Database,
            badge: "01",
            title: "Smart Migration™",
            desc: "Importi i pazienti da TeleRicetta, AlfaDocs, CSV o Excel in 60 secondi. Riconoscimento automatico campi, deduplica, normalizzazione date e telefoni.",
            highlights: ["Auto-mapping IT/EN", "Dedup intelligente", "GDPR ready · Audit log"],
            grad: "from-violet-500 to-fuchsia-500",
          },
          {
            icon: Activity,
            badge: "02",
            title: "Smart Odontogram™",
            desc: "Cartella interattiva 32 denti. Stati clinici colorati, timeline modifiche, side panel con storico e operatore. Touch-friendly, premium, GDPR.",
            highlights: ["8 stati clinici · numerazione FDI", "Audit log per ogni modifica", "Architettura AI-ready"],
            grad: "from-cyan-500 to-blue-600",
          },
          {
            icon: Sparkles,
            badge: "03",
            title: "Smart Records™ · AI",
            desc: "Cartella clinica AI con anamnesi strutturata, alert allergie, suggerimenti recall e piano cure. Powered by Claude Sonnet 4.5.",
            highlights: ["Riassunti AI clinici", "Alert prescrizioni", "Recall suggeriti automaticamente"],
            grad: "from-emerald-500 to-teal-500",
          },
          {
            icon: ScanLine,
            badge: "04",
            title: "Smart Patient Intake™",
            desc: "La receptionist fotografa carta d'identità o tessera sanitaria. L'AI Vision compila la scheda paziente in 10 secondi con codice fiscale validato.",
            highlights: ["OCR Claude Vision", "Validazione CF locale", "Zero errori digitazione"],
            grad: "from-amber-500 to-orange-500",
          },
          {
            icon: FileSignature,
            badge: "05",
            title: "Smart Consents™",
            desc: "Il paziente firma privacy, consenso sanitario e piano cure dal suo smartphone. Wizard mobile-first, PDF con audit-trail GDPR completo.",
            highlights: ["Firma su mobile · senza app", "QR · WhatsApp · Email share", "PDF con hash + IP + timestamp"],
            grad: "from-violet-500 to-blue-500",
          },
          {
            icon: Wallet,
            badge: "06",
            title: "Smart Billing™",
            desc: "Ricevute progressive PDF, stati pagamento, collegamento trattamenti. Architettura modulare già pronta per la futura integrazione XML SDI italiana.",
            highlights: ["Ricevute YYYY/NNNN", "Pagato · Parziale · Sospeso", "SDI-ready · 7 metodi pagamento"],
            grad: "from-sky-500 to-teal-600",
          },
        ].map((p, i) => (
          <div
            key={p.title}
            className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800/80 p-6 hover:border-violet-500/50 transition-all"
            data-testid={`dental-suite-pillar-${i}`}
          >
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.grad} flex items-center justify-center shadow-lg mb-4`}>
              <p.icon className="w-5 h-5 text-white" />
            </div>
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-violet-300/80">{p.badge}</span>
            <h3 className="text-xl font-bold text-white mt-1">{p.title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed mt-2">{p.desc}</p>
            <ul className="mt-5 space-y-1.5">
              {p.highlights.map((h, j) => (
                <li key={j} className="flex items-start gap-2 text-xs text-slate-300">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="text-center mt-12 flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={goDemo}
          data-testid="dental-suite-cta"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-bold text-sm shadow-2xl hover:shadow-violet-500/30 hover:scale-[1.02] transition-all"
        >
          <Play className="w-4 h-4 fill-white" />
          Vedi la Dental Suite in azione (3 minuti)
        </button>
        <a
          href="/smartbooker-dental-brochure.pdf"
          download="smartbooker-dental-brochure.pdf"
          data-testid="dental-suite-brochure"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-colors"
        >
          <FileText className="w-4 h-4" />
          Scarica brochure PDF
        </a>
      </div>
    </div>
  </section>
);

// ============================================================
// Features premium
// ============================================================
const FeaturesSection = () => (
  <section className="py-20 px-5" data-testid="features-section">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Funzionalità pensate per lo studio dentistico</h2>
        <p className="text-slate-400">Ogni dettaglio ottimizzato per il settore dentale.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {FEATURES_PREMIUM.map((f, i) => (
          <div key={i} className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/40 hover:border-cyan-500/40 transition-colors flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center">
              <f.icon className="w-4 h-4" />
            </div>
            <p className="text-xs sm:text-sm text-slate-200 font-medium leading-tight">{f.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// Social proof / bullet points
// ============================================================
const SocialProofSection = () => (
  <section className="py-20 px-5 bg-slate-900/30 border-y border-slate-800/40" data-testid="social-proof-section">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-1 text-amber-400 mb-2">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400" />)}
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold mb-3">Pensato per chi vuole uno studio moderno e organizzato</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {[
          "Riduce il caos in reception",
          "Migliora l'esperienza paziente",
          "Aumenta la presenza agli appuntamenti",
          "Migliora l'immagine digitale dello studio",
        ].map((b, i) => (
          <div key={i} className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/40 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <p className="text-sm text-slate-200 font-medium">{b}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// Comparison table
// ============================================================
const ComparisonSection = () => (
  <section className="py-20 px-5" data-testid="comparison-section">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-bold">Telefono tradizionale <span className="text-slate-500">vs</span> <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">SmartBooker</span></h2>
      </div>
      <div className="rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/40">
        <div className="grid grid-cols-2 bg-slate-800/60 text-xs sm:text-sm font-semibold">
          <div className="p-4 text-slate-400 flex items-center gap-2"><X className="w-4 h-4 text-red-500" /> Metodo tradizionale</div>
          <div className="p-4 text-cyan-300 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> SmartBooker</div>
        </div>
        {COMPARISON.map((row, i) => (
          <div key={i} className="grid grid-cols-2 border-t border-slate-700/40 text-sm">
            <div className="p-4 text-slate-400">{row[0]}</div>
            <div className="p-4 text-white font-medium">{row[1]}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// Final CTA
// ============================================================
const FinalCtaSection = ({ goTrial, goDemo }) => (
  <section className="relative py-24 px-5 overflow-hidden" data-testid="final-cta-section">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-b from-cyan-500/10 to-transparent" />
    </div>
    <div className="relative max-w-4xl mx-auto text-center">
      <h2 className="text-3xl sm:text-5xl font-bold leading-tight mb-4">
        Se il tuo studio <span className="text-amber-400">dipende ancora dal telefono</span>,
        <br className="hidden sm:block" /> stai perdendo tempo e pazienti.
      </h2>
      <p className="text-lg text-slate-300 mb-10">Automatizza oggi reception, agenda e richiami.</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={() => goTrial("final_cta")}
          className="h-14 px-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold text-base shadow-2xl shadow-cyan-500/40"
          data-testid="final-cta-trial"
        >
          <Sparkles className="w-5 h-5 mr-2" /> Prova gratuita 14 giorni
        </Button>
        <Button
          onClick={() => goDemo("final_cta")}
          variant="outline"
          className="h-14 px-8 rounded-full border-slate-600 bg-slate-800/40 hover:bg-slate-800 text-slate-100 font-semibold text-base"
          data-testid="final-cta-demo"
        >
          <Play className="w-5 h-5 mr-2" /> Guarda demo odontoiatrica
        </Button>
      </div>
    </div>
  </section>
);

// ============================================================
// FAQ
// ============================================================
const FaqSection = ({ track }) => {
  const [open, setOpen] = useState(-1);
  const toggle = (i) => {
    const next = open === i ? -1 : i;
    setOpen(next);
    if (next >= 0) track("faq_open", { index: i });
  };
  return (
    <section className="py-20 px-5 bg-slate-900/30 border-t border-slate-800/40" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10">Domande frequenti</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-2xl bg-slate-800/40 border border-slate-700/50 overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => toggle(i)} className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-800/60 transition-colors">
                <span className="font-semibold text-white text-sm sm:text-base pr-4">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-4 pb-4 text-sm text-slate-300 leading-relaxed border-t border-slate-700/40 pt-3">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// Exit popup
// ============================================================
const ExitPopup = ({ onClose, onDemo, onTrial }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200" data-testid="exit-popup" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="relative max-w-md w-full rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/40 p-7 shadow-2xl shadow-cyan-500/20">
      <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors" data-testid="exit-popup-close">
        <X className="w-4 h-4 text-slate-400" />
      </button>
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
          <Activity className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
          Vuoi vedere come SmartBooker riempie l'agenda del tuo studio?
        </h3>
        <p className="text-sm text-slate-400 mb-6">
          Scegli come iniziare — 100% gratis, nessuna carta.
        </p>
        <div className="space-y-2">
          <Button onClick={onDemo} className="w-full h-12 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-600 hover:from-violet-600 hover:to-fuchsia-700 text-white font-semibold" data-testid="exit-popup-demo">
            <Play className="w-4 h-4 mr-2" /> Guarda demo
          </Button>
          <Button onClick={onTrial} className="w-full h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold" data-testid="exit-popup-trial">
            <Sparkles className="w-4 h-4 mr-2" /> Prova gratis
          </Button>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================
// Pricing (Dental Pro — single plan premium)
// ============================================================
const PRICING_FEATURES = [
  "🦷 Smart Odontogram™ · cartella interattiva",
  "📥 Smart Migration™ · import da TeleRicetta/AlfaDocs",
  "🎖️ Smart Records · Cartella clinica AI",
  "📸 Smart Patient Intake™ · OCR documenti AI",
  "✍️ Smart Consents™ · firma digitale moduli",
  "💳 Smart Billing™ · ricevute PDF · SDI-ready",
  "Prenotazioni online H24",
  "Agenda poltrone multi-operatore",
  "WhatsApp AI (anche vocali)",
  "Modifiche e cancellazioni automatiche",
  "Reminder anti no-show",
  "Recall igiene automatici",
  "CRM pazienti completo",
  "Follow-up preventivi non chiusi",
  "Analytics studio dentistico",
  "Assistenza prioritaria",
  "Branding personalizzato studio",
];

const PricingSection = ({ track, goTrialPro, goDemoFromPricing, goEnterprise }) => {
  const sectionRef = useRef(null);
  const viewTrackedRef = useRef(false);
  const scrollTrackedRef = useRef(false);

  const isAd = useMemo(() => {
    if (typeof window === "undefined") return false;
    return /utm_|gclid|fbclid/i.test(window.location.search);
  }, []);

  useEffect(() => {
    if (!sectionRef.current) return;
    const el = sectionRef.current;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        if (!scrollTrackedRef.current) {
          track("scroll_to_pricing");
          scrollTrackedRef.current = true;
        }
        // pricing_view fires only when the card is significantly visible
        if (e.intersectionRatio >= 0.45 && !viewTrackedRef.current) {
          track("pricing_view");
          viewTrackedRef.current = true;
        }
      });
    }, { threshold: [0.1, 0.45] });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line
  }, []);

  return (
    <section id="pricing" className="relative py-20 sm:py-24 px-5 bg-gradient-to-b from-slate-950 via-blue-950/20 to-slate-950" data-testid="pricing-section">
      {/* Ads ribbon */}
      {isAd && (
        <div className="max-w-3xl mx-auto mb-6 text-center" data-testid="pricing-ribbon">
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-orange-500/15 text-amber-300 text-xs font-semibold">
            🎁 Onboarding dedicato per studi dentistici incluso gratuitamente questo mese
          </span>
        </div>
      )}

      <div ref={sectionRef} className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 text-white" data-testid="pricing-headline">
          Un solo appuntamento recuperato può ripagarti il piano mensile
        </h2>
        <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto mb-12">
          Per questo abbiamo creato una soluzione completa pensata per studi dentistici moderni.
        </p>

        {/* Premium pricing card */}
        <div className="relative group" data-testid="pricing-card">
          {/* Glow */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 rounded-[28px] blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />

          <div className="relative rounded-3xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-50 dark:to-white p-6 sm:p-10 text-slate-900 shadow-2xl">
            {/* Top badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold shadow-lg" data-testid="pricing-badge">
                <Crown className="w-3.5 h-3.5" /> Più scelto dagli studi dentistici
              </span>
            </div>

            {/* Plan name */}
            <div className="pt-4">
              <p className="text-xs font-semibold tracking-widest text-slate-500 uppercase">Il più Acquistato</p>
              <h3 className="mt-1 text-2xl sm:text-3xl font-bold" data-testid="pricing-plan-name">
                SmartBooker <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Dental Pro</span>
              </h3>
            </div>

            {/* Price */}
            <div className="mt-5 flex items-baseline justify-center gap-1" data-testid="pricing-price">
              <span className="text-5xl sm:text-6xl font-bold text-slate-900">€179</span>
              <span className="text-base font-medium text-slate-500">/mese</span>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              + IVA · 14 giorni di prova gratuita · Nessuna carta richiesta
            </p>

            {/* Value copy */}
            <div className="mt-6 mx-auto max-w-md px-4 py-3 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200/60">
              <p className="text-sm text-slate-700 italic leading-relaxed">
                Non è un costo operativo.<br />
                <span className="font-semibold text-slate-900">È uno strumento che libera tempo e genera appuntamenti.</span>
              </p>
            </div>

            {/* Feature list */}
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-left">
              {PRICING_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-800" data-testid={`pricing-feature-${i}`}>
                  <span className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  <span className="font-medium">{f}</span>
                </li>
              ))}
            </ul>

            {/* Primary CTA */}
            <div className="mt-8">
              <Button
                onClick={() => goTrialPro("pricing_card")}
                className="w-full h-14 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-base shadow-xl shadow-cyan-500/30"
                data-testid="pricing-cta-trial"
              >
                <Sparkles className="w-5 h-5 mr-2" /> Attiva prova gratuita 14 giorni
              </Button>
              <button
                onClick={goDemoFromPricing}
                className="mt-3 text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors inline-flex items-center gap-1"
                data-testid="pricing-cta-demo"
              >
                Guarda prima la demo odontoiatrica <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Microcopy */}
            <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Nessuna installazione</span>
              <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Attivo in pochi minuti</span>
              <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Supporto italiano</span>
            </div>
          </div>
        </div>

        {/* ROI boxes */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="pricing-roi-boxes">
          {[
            { icon: TrendingUp, text: "Recuperi 2 no-show = piano ripagato" },
            { icon: RefreshCw, text: "3 richiami igiene convertiti = profitto immediato" },
            { icon: Users, text: "Meno telefonate = più produttività reception" },
          ].map((r, i) => (
            <div key={i} className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/50 text-left" data-testid={`pricing-roi-${i}`}>
              <r.icon className="w-5 h-5 text-cyan-400 mb-2" />
              <p className="text-sm text-slate-200 font-medium leading-snug">{r.text}</p>
            </div>
          ))}
        </div>

        {/* Social proof */}
        <p className="mt-8 text-xs text-slate-500 italic">
          Pensato per studi dentistici che vogliono crescere e automatizzare.
        </p>

        {/* Enterprise secondary block */}
        <div className="mt-16 p-6 sm:p-7 rounded-3xl bg-slate-900/40 border border-slate-700/50 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 text-left" data-testid="pricing-enterprise">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6 text-slate-200" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white mb-1">Hai più sedi o una struttura avanzata?</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Disponiamo di soluzioni personalizzate per cliniche multi-sede e gruppi odontoiatrici.
            </p>
          </div>
          <Button
            onClick={goEnterprise}
            variant="outline"
            className="rounded-full border-slate-600 bg-slate-800/40 hover:bg-slate-700 text-slate-100 font-medium"
            data-testid="pricing-enterprise-cta"
          >
            <Headphones className="w-4 h-4 mr-2" /> Richiedi consulenza enterprise
          </Button>
        </div>
      </div>
    </section>
  );
};

export default DentistaLanding;
